import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { MrvDataset, MrvDatasetStatus, MrvSector, NdcProgress, NdcTarget, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const OFFICER_ROLES = ['ENV_OFFICER'];
const SECTORS: MrvSector[] = ['ENERGY', 'TRANSPORT', 'WASTE', 'AFOLU', 'INDUSTRIAL_PROCESSES'];

type Tab = 'mrv' | 'ndc';

/** MRV emissions-dataset ingestion (per sector/year) + NDC target/progress tracking (M31). */
export function Mrv() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ENV_OFFICER') ?? false;
  const [tab, setTab] = useState<Tab>('mrv');

  return (
    <div>
      <PageHeader title="MRV & NDC Tracking" subtitle="Sector emissions datasets (draft -> submitted -> verified -> published) + Nationally Determined Contribution progress (M31)." />
      <TabStrip tabs={[{ key: 'mrv', label: 'MRV datasets' }, { key: 'ndc', label: 'NDC targets' }]} active={tab} onChange={setTab} />
      {tab === 'mrv' ? <MrvTab isOfficer={isOfficer} /> : <NdcTab isOfficer={isOfficer} />}
    </div>
  );
}

function MrvTab({ isOfficer }: { isOfficer: boolean }) {
  const [sector, setSector] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (sector) params.set('sector', sector);
    return `/api/v1/m31/mrv-datasets?${params.toString()}`;
  }, [sector]);
  const listQ = useApiQuery<Page<MrvDataset>>(path);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <select className="field-input !w-auto" value={sector} onChange={(e) => setSector(e.target.value)}>
          <option value="">All sectors</option>
          {SECTORS.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        {isOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Record dataset
          </button>
        ) : null}
      </div>
      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No MRV datasets recorded" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Sector</th>
                <th className="table-head">Year</th>
                <th className="table-head">CO2e tonnes</th>
                <th className="table-head">Source</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(d.id)}>
                  <td className="table-cell">
                    <StatusBadge status={d.sector} />
                  </td>
                  <td className="table-cell tabular-nums">{d.reportingYear}</td>
                  <td className="table-cell tabular-nums">{d.co2eTonnes?.toLocaleString() ?? '—'}</td>
                  <td className="table-cell text-xs text-slate-500">{d.dataSource}</td>
                  <td className="table-cell">
                    <StatusBadge status={d.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? <DatasetDetailModal id={selectedId} isOfficer={isOfficer} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} /> : null}
      {showCreate ? (
        <CreateDatasetModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function DatasetDetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<MrvDataset>(`/api/v1/m31/mrv-datasets/${id}`);
  const d = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (path: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m31/mrv-datasets/${id}/${path}`);
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={d ? `${d.sector} ${d.reportingYear}` : 'MRV dataset'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {d ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Status" value={<StatusBadge status={d.status} />} />
            <Detail label="CO2e tonnes" value={d.co2eTonnes?.toLocaleString() ?? '—'} />
            <Detail label="Data source" value={d.dataSource} />
            {d.submittedAt ? <Detail label="Submitted" value={`${formatDateTime(d.submittedAt)} by ${d.submittedBy}`} /> : null}
            {d.verifiedAt ? <Detail label="Verified" value={`${formatDateTime(d.verifiedAt)} by ${d.verifiedBy}`} /> : null}
          </div>
          {d.breakdown ? (
            <div>
              <div className="field-label">Breakdown</div>
              <ul className="text-xs text-slate-600">
                {Object.entries(d.breakdown).map(([k, v]) => (
                  <li key={k}>
                    {k}: {v.toLocaleString()}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {isOfficer && d.status === 'DRAFT' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act('submit')}>
              Submit
            </button>
          ) : null}
          {isOfficer && d.status === 'SUBMITTED' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act('verify')}>
              Verify
            </button>
          ) : null}
          {isOfficer && d.status === 'VERIFIED' ? (
            <button className="btn-success" disabled={busy} onClick={() => act('publish')}>
              Publish
            </button>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function CreateDatasetModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [sector, setSector] = useState<MrvSector>('ENERGY');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear() - 1);
  const [co2eTonnes, setCo2eTonnes] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/mrv-datasets', {
        sector,
        reportingYear,
        co2eTonnes: Number.parseFloat(co2eTonnes),
        dataSource,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record dataset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Record emissions dataset" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sector">
            <select className="field-input" value={sector} onChange={(e) => setSector(e.target.value as MrvSector)}>
              {SECTORS.map((s) => (
                <option key={s} value={s}>
                  {s.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reporting year">
            <input className="field-input" type="number" value={reportingYear} onChange={(e) => setReportingYear(Number.parseInt(e.target.value, 10) || reportingYear)} />
          </Field>
        </div>
        <Field label="CO2e tonnes">
          <input className="field-input" type="number" step="0.001" value={co2eTonnes} onChange={(e) => setCo2eTonnes(e.target.value)} />
        </Field>
        <Field label="Data source">
          <input className="field-input" value={dataSource} onChange={(e) => setDataSource(e.target.value)} placeholder="e.g. PUC generation returns, Customs fuel import data" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !co2eTonnes || dataSource.trim().length < 2} onClick={submit}>
            {busy ? 'Saving…' : 'Record dataset'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function NdcTab({ isOfficer }: { isOfficer: boolean }) {
  const targetsQ = useApiQuery<{ items: NdcTarget[] }>('/api/v1/m31/ndc-targets?active=true');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {isOfficer ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New NDC target
          </button>
        ) : null}
      </div>
      {targetsQ.error ? <ErrorNote message={targetsQ.error} /> : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          {targetsQ.loading ? (
            <Spinner />
          ) : (targetsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No active NDC targets" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Code</th>
                  <th className="table-head">Target</th>
                  <th className="table-head">Unit</th>
                </tr>
              </thead>
              <tbody>
                {targetsQ.data!.items.map((t) => (
                  <tr key={t.code} className={`cursor-pointer hover:bg-slate-50 ${selectedCode === t.code ? 'bg-brand-50' : ''}`} onClick={() => setSelectedCode(t.code)}>
                    <td className="table-cell font-mono text-xs font-medium">{t.code}</td>
                    <td className="table-cell text-xs text-slate-500">
                      {t.baselineYear}: {t.baselineValue ?? '—'} → {t.targetYear}: {t.targetValue ?? '—'}
                    </td>
                    <td className="table-cell text-xs text-slate-500">{t.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {selectedCode ? <NdcProgressPanel code={selectedCode} isOfficer={isOfficer} /> : <EmptyState title="Select an NDC target to see its progress history" />}
      </div>
      {showCreate ? (
        <CreateTargetModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            targetsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NdcProgressPanel({ code, isOfficer }: { code: string; isOfficer: boolean }) {
  const q = useApiQuery<{ target: NdcTarget; items: NdcProgress[] }>(`/api/v1/m31/ndc-targets/${code}/progress`);
  const [year, setYear] = useState(new Date().getFullYear());
  const [actualValue, setActualValue] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m31/ndc-targets/${code}/progress`, { year, actualValue: Number.parseFloat(actualValue), notes: notes || undefined });
      setActualValue('');
      setNotes('');
      q.reload();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to record progress');
    } finally {
      setBusy(false);
    }
  };

  const t = q.data?.target;
  const items = q.data?.items ?? [];
  const target = t?.targetValue ?? null;
  const baseline = t?.baselineValue ?? null;
  const maxVal = Math.max(1, ...items.map((i) => i.actualValue ?? 0), target ?? 0, baseline ?? 0);

  return (
    <div className="card p-4">
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? (
        <Spinner />
      ) : (
        <>
          <div className="mb-2 text-sm font-bold text-slate-700">{t?.description}</div>
          <p className="mb-3 text-xs text-slate-500">
            Baseline {t?.baselineYear}: {baseline ?? '—'} {t?.unit} → Target {t?.targetYear}: {target ?? '—'} {t?.unit}
          </p>
          <div className="mb-3 flex h-32 items-end gap-2">
            {items.length === 0 ? (
              <p className="text-xs text-slate-400">No yearly progress recorded yet.</p>
            ) : (
              items.map((p) => (
                <div key={p.id} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-[10px] font-semibold tabular-nums text-slate-600">{p.actualValue?.toLocaleString() ?? '—'}</div>
                  <div className="w-full rounded-t bg-brand-500" style={{ height: `${Math.max(4, ((p.actualValue ?? 0) / maxVal) * 100)}%` }} />
                  <div className="text-[10px] text-slate-400">{p.year}</div>
                </div>
              ))
            )}
          </div>
          {isOfficer ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              {error ? <ErrorNote message={error} /> : null}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Year">
                  <input className="field-input" type="number" value={year} onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || year)} />
                </Field>
                <Field label={`Actual value (${t?.unit ?? ''})`}>
                  <input className="field-input" type="number" step="0.001" value={actualValue} onChange={(e) => setActualValue(e.target.value)} />
                </Field>
              </div>
              <Field label="Notes (optional)">
                <input className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              <button className="btn-primary" disabled={busy || !actualValue} onClick={submit}>
                {busy ? 'Saving…' : 'Record year progress'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function CreateTargetModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [baselineYear, setBaselineYear] = useState(2015);
  const [baselineValue, setBaselineValue] = useState('');
  const [targetYear, setTargetYear] = useState(2030);
  const [targetValue, setTargetValue] = useState('');
  const [unit, setUnit] = useState('ktCO2e');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/ndc-targets', {
        code,
        description,
        baselineYear,
        baselineValue: Number.parseFloat(baselineValue),
        targetYear,
        targetValue: Number.parseFloat(targetValue),
        unit,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create target');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New NDC target" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. NDC_2030_ECONOMY_WIDE" />
          </Field>
          <Field label="Unit">
            <input className="field-input" value={unit} onChange={(e) => setUnit(e.target.value)} />
          </Field>
        </div>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Baseline year">
            <input className="field-input" type="number" value={baselineYear} onChange={(e) => setBaselineYear(Number.parseInt(e.target.value, 10) || baselineYear)} />
          </Field>
          <Field label="Baseline value">
            <input className="field-input" type="number" step="0.001" value={baselineValue} onChange={(e) => setBaselineValue(e.target.value)} />
          </Field>
          <Field label="Target year">
            <input className="field-input" type="number" value={targetYear} onChange={(e) => setTargetYear(Number.parseInt(e.target.value, 10) || targetYear)} />
          </Field>
          <Field label="Target value">
            <input className="field-input" type="number" step="0.001" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || code.trim().length < 2 || description.trim().length < 3 || !baselineValue || !targetValue} onClick={submit}>
            {busy ? 'Creating…' : 'Create target'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
